import type {
  BomComponentInput,
  BomComponentRow,
  BomReplacementDraftProtocol,
  KitBom,
} from "../api/bom";
import { canonicalBomReplacementClaimToken } from "../api/bom";
import { canonicalShopDomain } from "../api/client";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../api/stocky_public_ids";

const STORAGE_PREFIX = "ub-bom-stale-replacement:v1:";
// Keep the original key so older evidence is never mistaken for an empty
// workflow. Exact v2 envelopes are migrated as legacy-native-create evidence;
// malformed/v1 envelopes remain fail-closed.
const STORAGE_VERSION = 3;
const LEGACY_STORAGE_VERSION = 2;
const MAX_BUNDLE_COMPONENTS = 30;

export class BomStaleReplacementCheckpointStorageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BomStaleReplacementCheckpointStorageError";
  }
}

export interface CanonicalBomComponentRow
  extends Omit<
    BomComponentRow,
    "component_variant_id" | "shopify_variant_id"
  > {
  component_variant_id: string;
  shopify_variant_id: string;
}

export interface CanonicalKitBom
  extends Omit<
    KitBom,
    | "parent_variant_id"
    | "shopify_variant_id"
    | "components"
    | "limiting_component_variant_id"
  > {
  parent_variant_id: string;
  shopify_variant_id: string;
  components: CanonicalBomComponentRow[];
  limiting_component_variant_id: string | null;
}

export interface CanonicalBomComponentInput
  extends Omit<BomComponentInput, "component_shopify_variant_id"> {
  component_shopify_variant_id: string;
}

interface CheckpointBase {
  version: typeof STORAGE_VERSION;
  shopGeneration: string;
  /** Exact opaque token returned by the server-authoritative replacement claim. */
  workflowId: string;
  /** Creation provenance; only the server protocol may call the draft API. */
  draftProtocol: BomReplacementDraftProtocol;
  sourceKit: CanonicalKitBom;
  beforeVariantIds: string[];
}

/**
 * For `server_product_set_v1`, `intentCompleted=false` means the merchant has
 * not completed the editor for the claim-owned DRAFT. For
 * `legacy_native_create`, either value is only historical native-intent
 * evidence: callers may reconcile/sync/select, but must never create a product
 * or call the server draft endpoint. A bound target is always exact.
 */
export interface BomIntentOrSyncCheckpoint extends CheckpointBase {
  phase: "intent-or-sync";
  intentCompleted: boolean;
  /** Target already bound by another tab/replay, if the server disclosed one. */
  claimedTargetShopifyVariantId: string | null;
}

interface BomTargetCheckpointBase extends CheckpointBase {
  targetShopifyVariantId: string;
  components: CanonicalBomComponentInput[];
}

/** Exact payload which may be (re)sent to the idempotent Shopify bundle API. */
export interface BomTargetWriteCheckpoint extends BomTargetCheckpointBase {
  phase: "target-write";
}

/** Shopify + local target save succeeded; only local stale-BOM cleanup remains. */
export interface BomCleanupCheckpoint extends BomTargetCheckpointBase {
  phase: "cleanup";
}

export type BomStaleReplacementCheckpoint =
  | BomIntentOrSyncCheckpoint
  | BomTargetWriteCheckpoint
  | BomCleanupCheckpoint;

interface CheckpointInputBase {
  sourceKit: KitBom;
  beforeVariantIds: readonly StockyPublicBigIntId[];
}

export interface BomIntentOrSyncCheckpointInput extends CheckpointInputBase {
  phase: "intent-or-sync";
  draftProtocol: BomReplacementDraftProtocol;
  intentCompleted: boolean;
  claimedTargetShopifyVariantId: StockyPublicBigIntId | null;
}

interface BomTargetCheckpointInputBase extends CheckpointInputBase {
  targetShopifyVariantId: StockyPublicBigIntId;
  components: readonly BomComponentInput[];
}

export interface BomTargetWriteCheckpointInput
  extends BomTargetCheckpointInputBase {
  phase: "target-write";
}

export interface BomCleanupCheckpointInput extends BomTargetCheckpointInputBase {
  phase: "cleanup";
}

export type BomStaleReplacementCheckpointInput =
  | BomIntentOrSyncCheckpointInput
  | BomTargetWriteCheckpointInput
  | BomCleanupCheckpointInput;

function storageError(message: string): BomStaleReplacementCheckpointStorageError {
  return new BomStaleReplacementCheckpointStorageError(message);
}

function requireWorkflowId(value: unknown): string {
  try {
    return canonicalBomReplacementClaimToken(value);
  } catch {
    throw storageError("replacement claim token is invalid");
  }
}

function requireDraftProtocol(value: unknown): BomReplacementDraftProtocol {
  if (
    value !== "server_product_set_v1" &&
    value !== "legacy_native_create"
  ) {
    throw storageError("replacement draft protocol is invalid");
  }
  return value;
}

function requireShopGeneration(shopGeneration: string): string {
  const separator = shopGeneration.indexOf(":");
  const shopId = shopGeneration.slice(0, separator);
  const domainText = shopGeneration.slice(separator + 1);
  const domain = canonicalShopDomain(domainText);
  if (
    separator < 1 ||
    !isStockyPublicBigIntId(shopId) ||
    domain === null ||
    `${shopId}:${domain}` !== shopGeneration
  ) {
    throw storageError("verified shop generation is invalid");
  }
  return shopGeneration;
}

function storageKey(shopGeneration: string): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(
    requireShopGeneration(shopGeneration),
  )}`;
}

function sessionStorageOrThrow(): Storage {
  try {
    if (typeof window === "undefined") {
      throw new Error("window is unavailable");
    }
    return window.sessionStorage;
  } catch (error) {
    throw storageError(
      error instanceof Error ? error.message : "session storage is unavailable",
    );
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(
  value: Record<string, unknown>,
  expected: readonly string[],
): boolean {
  const actual = Object.keys(value).sort();
  const sortedExpected = [...expected].sort();
  return (
    actual.length === sortedExpected.length &&
    actual.every((key, index) => key === sortedExpected[index])
  );
}

function requireInteger(value: unknown, name: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw storageError(`${name} must be a safe integer`);
  }
  return value;
}

function requirePositiveInteger(value: unknown, name: string): number {
  const integer = requireInteger(value, name);
  if (integer < 1) throw storageError(`${name} must be positive`);
  return integer;
}

function requireNullableText(value: unknown, name: string): string | null {
  if (value !== null && typeof value !== "string") {
    throw storageError(`${name} must be text or null`);
  }
  return value;
}

function canonicalId(
  value: unknown,
  name: string,
  requireStoredString: boolean,
): string {
  if (requireStoredString && typeof value !== "string") {
    throw storageError(`${name} must be an exact BIGINT string`);
  }
  try {
    return canonicalStockyPublicBigIntId(value);
  } catch {
    throw storageError(`${name} must be an exact BIGINT string`);
  }
}

function normalizeComponentRow(
  value: unknown,
  requireStoredStrings: boolean,
): CanonicalBomComponentRow {
  if (!isRecord(value)) throw storageError("source component is invalid");
  if (
    requireStoredStrings &&
    !hasExactKeys(value, [
      "component_variant_id",
      "shopify_variant_id",
      "sku",
      "title",
      "quantity_per",
      "available",
      "buildable_from_this",
    ])
  ) {
    throw storageError("source component shape is invalid");
  }
  return {
    component_variant_id: canonicalId(
      value.component_variant_id,
      "source component id",
      requireStoredStrings,
    ),
    shopify_variant_id: canonicalId(
      value.shopify_variant_id,
      "source component Shopify id",
      requireStoredStrings,
    ),
    sku: requireNullableText(value.sku, "source component SKU"),
    title: requireNullableText(value.title, "source component title"),
    quantity_per: requirePositiveInteger(
      value.quantity_per,
      "source component quantity",
    ),
    available: requireInteger(value.available, "source component availability"),
    buildable_from_this: requireInteger(
      value.buildable_from_this,
      "source component buildable quantity",
    ),
  };
}

function normalizeSourceKit(
  value: unknown,
  requireStoredStrings: boolean,
): CanonicalKitBom {
  if (!isRecord(value)) throw storageError("source BOM is invalid");
  if (
    requireStoredStrings &&
    !hasExactKeys(value, [
      "parent_variant_id",
      "shopify_variant_id",
      "sku",
      "title",
      "components",
      "buildable_quantity",
      "limiting_component_variant_id",
    ])
  ) {
    throw storageError("source BOM shape is invalid");
  }
  if (!Array.isArray(value.components)) {
    throw storageError("source BOM components are invalid");
  }
  if (value.components.length > MAX_BUNDLE_COMPONENTS) {
    throw storageError("source BOM has too many components");
  }
  const components = value.components.map((component) =>
    normalizeComponentRow(component, requireStoredStrings),
  );
  const internalIds = new Set(components.map((row) => row.component_variant_id));
  const shopifyIds = new Set(components.map((row) => row.shopify_variant_id));
  if (
    internalIds.size !== components.length ||
    shopifyIds.size !== components.length
  ) {
    throw storageError("source BOM contains duplicate components");
  }
  const limitingComponentId =
    value.limiting_component_variant_id === null
      ? null
      : canonicalId(
          value.limiting_component_variant_id,
          "source limiting component id",
          requireStoredStrings,
        );
  if (
    limitingComponentId !== null &&
    !internalIds.has(limitingComponentId)
  ) {
    throw storageError("source limiting component is not in the BOM");
  }
  const buildableQuantity =
    value.buildable_quantity === null
      ? null
      : requireInteger(value.buildable_quantity, "source buildable quantity");
  return {
    parent_variant_id: canonicalId(
      value.parent_variant_id,
      "source parent id",
      requireStoredStrings,
    ),
    shopify_variant_id: canonicalId(
      value.shopify_variant_id,
      "source Shopify id",
      requireStoredStrings,
    ),
    sku: requireNullableText(value.sku, "source SKU"),
    title: requireNullableText(value.title, "source title"),
    components,
    buildable_quantity: buildableQuantity,
    limiting_component_variant_id: limitingComponentId,
  };
}

function normalizeIdList(
  value: unknown,
  name: string,
  requireStoredStrings: boolean,
): string[] {
  if (!Array.isArray(value)) throw storageError(`${name} is invalid`);
  const ids = value.map((id) =>
    canonicalId(id, name, requireStoredStrings),
  );
  if (new Set(ids).size !== ids.length) {
    throw storageError(`${name} contains duplicates`);
  }
  return ids;
}

function normalizeTargetComponents(
  value: unknown,
  targetId: string,
  requireStoredStrings: boolean,
): CanonicalBomComponentInput[] {
  if (!Array.isArray(value) || value.length < 1) {
    throw storageError("target components are required");
  }
  if (value.length > MAX_BUNDLE_COMPONENTS) {
    throw storageError("target has too many components");
  }
  const components = value.map((raw) => {
    if (!isRecord(raw)) throw storageError("target component is invalid");
    if (
      requireStoredStrings &&
      !hasExactKeys(raw, ["component_shopify_variant_id", "quantity_per"])
    ) {
      throw storageError("target component shape is invalid");
    }
    return {
      component_shopify_variant_id: canonicalId(
        raw.component_shopify_variant_id,
        "target component Shopify id",
        requireStoredStrings,
      ),
      quantity_per: requirePositiveInteger(
        raw.quantity_per,
        "target component quantity",
      ),
    };
  });
  const ids = components.map((component) =>
    component.component_shopify_variant_id,
  );
  if (new Set(ids).size !== ids.length) {
    throw storageError("target contains duplicate components");
  }
  if (ids.includes(targetId)) {
    throw storageError("target cannot contain itself");
  }
  return components;
}

function normalizeCheckpoint(
  shopGeneration: string,
  value: unknown,
  requireStoredShape: boolean,
): BomStaleReplacementCheckpoint {
  if (!isRecord(value)) throw storageError("replacement checkpoint is invalid");
  const generation = requireShopGeneration(shopGeneration);
  if (
    requireStoredShape &&
    (value.version !== STORAGE_VERSION || value.shopGeneration !== generation)
  ) {
    throw storageError("replacement checkpoint belongs to another shop");
  }
  const sourceKit = normalizeSourceKit(value.sourceKit, requireStoredShape);
  const workflowId = requireWorkflowId(value.workflowId);
  const draftProtocol = requireDraftProtocol(value.draftProtocol);
  const beforeVariantIds = normalizeIdList(
    value.beforeVariantIds,
    "catalog variant ids",
    requireStoredShape,
  );
  if (beforeVariantIds.includes(sourceKit.shopify_variant_id)) {
    throw storageError("stale source appears in the current catalog snapshot");
  }

  if (value.phase === "intent-or-sync") {
    if (
      requireStoredShape &&
      !hasExactKeys(value, [
        "version",
        "shopGeneration",
        "workflowId",
        "draftProtocol",
        "phase",
        "intentCompleted",
        "claimedTargetShopifyVariantId",
        "sourceKit",
        "beforeVariantIds",
      ])
    ) {
      throw storageError("intent checkpoint shape is invalid");
    }
    if (typeof value.intentCompleted !== "boolean") {
      throw storageError("intent completion state is invalid");
    }
    const claimedTargetShopifyVariantId =
      value.claimedTargetShopifyVariantId === null
        ? null
        : canonicalId(
            value.claimedTargetShopifyVariantId,
            "claimed target Shopify id",
            requireStoredShape,
          );
    if (
      claimedTargetShopifyVariantId === sourceKit.shopify_variant_id
    ) {
      throw storageError("claimed target cannot be the stale source");
    }
    return {
      version: STORAGE_VERSION,
      shopGeneration: generation,
      workflowId,
      draftProtocol,
      phase: value.phase,
      intentCompleted: value.intentCompleted,
      claimedTargetShopifyVariantId,
      sourceKit,
      beforeVariantIds,
    };
  }

  if (value.phase !== "target-write" && value.phase !== "cleanup") {
    throw storageError("replacement checkpoint phase is invalid");
  }
  if (
    requireStoredShape &&
    !hasExactKeys(value, [
      "version",
      "shopGeneration",
      "workflowId",
      "draftProtocol",
      "phase",
      "sourceKit",
      "beforeVariantIds",
      "targetShopifyVariantId",
      "components",
    ])
  ) {
    throw storageError("target checkpoint shape is invalid");
  }
  const targetId = canonicalId(
    value.targetShopifyVariantId,
    "target Shopify id",
    requireStoredShape,
  );
  if (
    targetId === sourceKit.shopify_variant_id ||
    beforeVariantIds.includes(targetId)
  ) {
    throw storageError("replacement target is not a newly selected product");
  }
  const components = normalizeTargetComponents(
    value.components,
    targetId,
    requireStoredShape,
  );
  return {
    version: STORAGE_VERSION,
    shopGeneration: generation,
    workflowId,
    draftProtocol,
    phase: value.phase,
    sourceKit,
    beforeVariantIds,
    targetShopifyVariantId: targetId,
    components,
  };
}

function migrateLegacyV2Checkpoint(
  shopGeneration: string,
  value: Record<string, unknown>,
): BomStaleReplacementCheckpoint {
  const commonKeys = [
    "version",
    "shopGeneration",
    "workflowId",
    "phase",
    "sourceKit",
    "beforeVariantIds",
  ];
  const expectedKeys =
    value.phase === "intent-or-sync"
      ? [
          ...commonKeys,
          "intentCompleted",
          "claimedTargetShopifyVariantId",
        ]
      : value.phase === "target-write" || value.phase === "cleanup"
        ? [...commonKeys, "targetShopifyVariantId", "components"]
        : null;
  if (
    value.version !== LEGACY_STORAGE_VERSION ||
    expectedKeys === null ||
    !hasExactKeys(value, expectedKeys)
  ) {
    throw storageError("legacy replacement checkpoint shape is invalid");
  }
  return normalizeCheckpoint(
    shopGeneration,
    {
      ...value,
      version: STORAGE_VERSION,
      draftProtocol: "legacy_native_create",
    },
    true,
  );
}

function removeOnlyThisCheckpoint(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch (error) {
    throw storageError(
      error instanceof Error ? error.message : "session storage cleanup failed",
    );
  }
}

/**
 * Load only the exact verified shop generation. A malformed or mismatched
 * envelope is a safety failure, not an empty state: it may be the only durable
 * evidence that Shopify product creation or editing already happened. Leave it in place
 * and fail closed rather than allowing a duplicate create on the next reload.
 */
export function loadBomStaleReplacementCheckpoint(
  shopGeneration: string,
): BomStaleReplacementCheckpoint | null {
  const generation = requireShopGeneration(shopGeneration);
  const key = storageKey(generation);
  const storage = sessionStorageOrThrow();
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch (error) {
    throw storageError(
      error instanceof Error ? error.message : "session storage read failed",
    );
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    throw storageError(
      error instanceof Error
        ? error.message
        : "replacement checkpoint JSON is invalid",
    );
  }
  if (isRecord(parsed) && parsed.version === LEGACY_STORAGE_VERSION) {
    const migrated = migrateLegacyV2Checkpoint(generation, parsed);
    const serialized = JSON.stringify(migrated);
    try {
      // Do not overwrite evidence changed by another same-tab callback or
      // embedded context between the read and migration.
      if (storage.getItem(key) !== raw) {
        throw storageError("legacy checkpoint migration compare-and-swap failed");
      }
      storage.setItem(key, serialized);
      if (storage.getItem(key) !== serialized) {
        throw storageError("legacy checkpoint migration readback mismatch");
      }
    } catch (error) {
      if (error instanceof BomStaleReplacementCheckpointStorageError) {
        throw error;
      }
      throw storageError(
        error instanceof Error
          ? error.message
          : "legacy checkpoint migration failed",
      );
    }
    return migrated;
  }
  return normalizeCheckpoint(generation, parsed, true);
}

/**
 * Persist before an external side effect. Write + exact readback is mandatory.
 * Server claims use this before draft creation/edit; legacy claims use it
 * before sync-only recovery and never authorize another product create.
 */
export function saveBomStaleReplacementCheckpoint(
  shopGeneration: string,
  claimToken: string,
  input: BomIntentOrSyncCheckpointInput,
): BomIntentOrSyncCheckpoint {
  const generation = requireShopGeneration(shopGeneration);
  if (input.intentCompleted) {
    throw storageError("a replacement workflow must start before the intent");
  }
  // A new server-draft/edit flow must never replace evidence from an earlier
  // same-shop workflow. sessionStorage is synchronous and tab-scoped, so this
  // read/check/write sequence is an atomic compare-with-empty boundary for all
  // callbacks in this SPA document.
  if (loadBomStaleReplacementCheckpoint(generation) !== null) {
    throw storageError("a replacement checkpoint already exists");
  }
  const checkpoint = normalizeCheckpoint(
    generation,
    {
      ...input,
      version: STORAGE_VERSION,
      shopGeneration: generation,
      workflowId: requireWorkflowId(claimToken),
    },
    false,
  );
  return writeCheckpointWithReadback(
    generation,
    checkpoint,
  ) as BomIntentOrSyncCheckpoint;
}

function exactCheckpointEquals(
  left: BomStaleReplacementCheckpoint,
  right: BomStaleReplacementCheckpoint,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function writeCheckpointWithReadback(
  generation: string,
  checkpoint: BomStaleReplacementCheckpoint,
): BomStaleReplacementCheckpoint {
  const storage = sessionStorageOrThrow();
  const key = storageKey(generation);
  try {
    storage.setItem(key, JSON.stringify(checkpoint));
  } catch (error) {
    throw storageError(
      error instanceof Error ? error.message : "session storage write failed",
    );
  }
  const readback = loadBomStaleReplacementCheckpoint(generation);
  if (readback === null || JSON.stringify(readback) !== JSON.stringify(checkpoint)) {
    throw storageError("session storage readback mismatch");
  }
  return readback;
}

function requireSameWorkflowEvidence(
  previous: BomStaleReplacementCheckpoint,
  next: BomStaleReplacementCheckpoint,
): void {
  if (
    previous.workflowId !== next.workflowId ||
    previous.draftProtocol !== next.draftProtocol ||
    JSON.stringify(previous.sourceKit) !== JSON.stringify(next.sourceKit) ||
    JSON.stringify(previous.beforeVariantIds) !==
      JSON.stringify(next.beforeVariantIds)
  ) {
    throw storageError("replacement workflow evidence changed");
  }
  if (previous.phase === "cleanup") {
    throw storageError("a completed target save cannot transition again");
  }
  if (previous.phase === "intent-or-sync") {
    if (next.phase === "intent-or-sync") {
      if (previous.draftProtocol === "legacy_native_create") {
        const completionAdvanced =
          !previous.intentCompleted && next.intentCompleted;
        const targetAdvanced =
          previous.claimedTargetShopifyVariantId === null &&
          next.claimedTargetShopifyVariantId !== null;
        if (
          (previous.intentCompleted && !next.intentCompleted) ||
          (previous.claimedTargetShopifyVariantId !== null &&
            previous.claimedTargetShopifyVariantId !==
              next.claimedTargetShopifyVariantId) ||
          (!completionAdvanced && !targetAdvanced)
        ) {
          throw storageError("legacy intent checkpoint transition is invalid");
        }
        return;
      }
      if (
        previous.intentCompleted ||
        !next.intentCompleted ||
        previous.claimedTargetShopifyVariantId !==
          next.claimedTargetShopifyVariantId
      ) {
        throw storageError("intent checkpoint transition is invalid");
      }
      return;
    }
    if (
      previous.claimedTargetShopifyVariantId !== null &&
      previous.claimedTargetShopifyVariantId !==
        next.targetShopifyVariantId
    ) {
      throw storageError("target does not match the server-bound claim");
    }
    return;
  }
  if (next.phase === "target-write") {
    if (!exactCheckpointEquals(previous, next)) {
      throw storageError("target write retry must be byte-exact");
    }
    return;
  }
  if (previous.phase !== "target-write" || next.phase !== "cleanup") {
    throw storageError("target write checkpoint transition is invalid");
  }
  if (
    previous.targetShopifyVariantId !== next.targetShopifyVariantId ||
    JSON.stringify(previous.components) !== JSON.stringify(next.components)
  ) {
    throw storageError("cleanup checkpoint must match the target write");
  }
}

/**
 * Advance one exact workflow. A delayed callback holding `expected` cannot
 * overwrite a newer same-shop workflow because the current stored envelope
 * must still match byte-for-byte before this synchronous write.
 */
export function transitionBomStaleReplacementCheckpoint(
  shopGeneration: string,
  expected: BomStaleReplacementCheckpoint,
  input: BomStaleReplacementCheckpointInput,
): BomStaleReplacementCheckpoint {
  const generation = requireShopGeneration(shopGeneration);
  const normalizedExpected = normalizeCheckpoint(generation, expected, true);
  const current = loadBomStaleReplacementCheckpoint(generation);
  if (current === null || !exactCheckpointEquals(current, normalizedExpected)) {
    throw storageError("replacement checkpoint compare-and-swap failed");
  }
  const next = normalizeCheckpoint(
    generation,
    {
      ...input,
      version: STORAGE_VERSION,
      shopGeneration: generation,
      workflowId: current.workflowId,
      draftProtocol: current.draftProtocol,
    },
    false,
  );
  requireSameWorkflowEvidence(current, next);
  return writeCheckpointWithReadback(generation, next);
}

/** Clear only the exact continuation owned by the caller. */
export function clearBomStaleReplacementCheckpoint(
  shopGeneration: string,
  expected: BomStaleReplacementCheckpoint,
): void {
  const generation = requireShopGeneration(shopGeneration);
  const normalizedExpected = normalizeCheckpoint(generation, expected, true);
  const storage = sessionStorageOrThrow();
  const key = storageKey(generation);
  const current = loadBomStaleReplacementCheckpoint(generation);
  if (current === null || !exactCheckpointEquals(current, normalizedExpected)) {
    throw storageError("replacement checkpoint compare-and-clear failed");
  }
  removeOnlyThisCheckpoint(storage, key);
  try {
    if (storage.getItem(key) !== null) {
      throw storageError("session storage cleanup readback mismatch");
    }
  } catch (error) {
    if (error instanceof BomStaleReplacementCheckpointStorageError) {
      throw error;
    }
    throw storageError(
      error instanceof Error ? error.message : "session storage cleanup failed",
    );
  }
}
